import type { FetchError } from "@selector/shared";
import {
  AlignmentDensityEnum,
  type components,
  type GenomicFeatureTypeEnum,
  PileupRenderingModeEnum,
  SyntenyTypeEnum,
  TrackEnum,
  TrackTextEnum,
} from "@selector/shared/openapi";
import type { GridApi } from "ag-grid-community";
import { clamp } from "lodash-es";
import type { RefObject } from "react";
import type { StateCreator } from "zustand";

import {
  fetchFeaturesFamilyMembersOrThrow,
  fetchPangenomeAlignmentMembersByFeatureOrThrow,
  fetchPangenomeAlignmentMembersOrThrow,
  getFeatureTypes,
} from "../../api/fetch.ts";
import { MIN_BASE_PER_PIXEL, MIN_FOCUSED_SHAPE_WIDTH } from "../../settings.ts";
import type { MiniFamily } from "../../synteny-worker/state.ts";
import { SHAPE_HEIGHT_WITHOUT_TEXT } from "../../track-workers/graphics/settings.ts";
import type {
  AlignmentDensity,
  CanvasCoordinate,
  MainThreadHoverMessage,
  MinimalItem,
  NavigationFeature,
  OrthologousGroup,
  PileupRenderingMode,
  Track,
  TrackGroup,
  TrackGroups,
  Tracks,
  TrackType,
} from "../../types.ts";
import { getColorByTrackType } from "../../utils/graphics/color-conversion.ts";
import { getTrackOffsetY } from "../../utils/graphics/track-offset.ts";
import { type NUFeatureOptions, type TrackConfig } from "../../utils/tracks/create-tracks.ts";
import { calculateContigSwaps } from "../../utils/tracks/track-groups-with-feature-elsewhere.ts";
import {
  getGenomicFeatureTypeFromTrackType,
  getTrackTypeBySyntenyType,
  getTrackTypeFromGenomicFeatureType,
} from "../../utils/type-conversion.ts";
import { createTracksLoader } from "../load-tracks.ts";
import type { GenomeViewerState, GenomeViewerStore } from "../store.ts";
import { initialCanvasState } from "./canvas-slice.ts";
import { initialRenderingState } from "./rendering-slice.ts";
import { initialSaveLoadState } from "./save-load-slice.ts";
import { initialSearchState } from "./search-slice.ts";
import { initialSelectionState } from "./selection-slice.ts";
import { initialTracksState } from "./tracks-slice.ts";
import { handleScrollingEnd, initialUserEventsState } from "./user-events-slice.ts";

export interface MemberItem extends MinimalItem {
  trackGroupId: string;
  familyId?: number;
  contigId: number;
}

export interface ContigSwapUpdate {
  groupId: string;
  newContigId: number;
  toBase?: number;
}

export interface ComputeSyntenyTrackAlignmentsData {
  fromMemberItem: MemberItem;
  trackType: TrackType | undefined;
  trackGroups: TrackGroups;
  tracks: Tracks;
  alignedFamily: MiniFamily | undefined;
  alignedTrackGroupStarts: Map<string, number> | undefined;
  newTrackGroups: ContigSwapUpdate[];
  familyMembers: Awaited<ReturnType<typeof fetchFeaturesFamilyMembersOrThrow>>;
}

export interface ComputeSyntenyTrackAlignmentsResult {
  newAlignedFamily: MiniFamily | undefined;
  newAlignedTrackGroupStarts: Map<string, number>;
}

export interface MainState {
  /** All tracks for all genomes. Knows in which group it belongs to */
  tracks: Tracks;
  /** A combination of genome + contig + some group settings. Contains a list of track ids */
  trackGroups: TrackGroups;
  /** The main HTML Canvas on the main thread */
  canvas?: HTMLCanvasElement;
  /** The main CanvasRenderingContext2D on the main thread */
  ctx?: CanvasRenderingContext2D;
  /** The currently hovered group id. Use between the main canvas, the tracks panel and the overview */
  hoveredGroupId?: string;
  hoveredTackId?: string;
  /** The currently loaded synteny organism */
  syntenyOrganism: {
    id: number | undefined;
    name: string | undefined;
    type: components["schemas"]["SequencingTypeEnum"] | undefined;
  };
  /** All genomes with all their contigs */
  organisms: Map<number, { id: number; name: string; contigs: Map<number, components["schemas"]["ContigResponse"]> }>;
  /** The alignment image is a big canvas / layer we receive from another worker */
  alignmentImageBitmap?: ImageBitmap;
  alignmentImageBitmapScrollX: number;
  alignmentImageBitmapScrollY: number;
  alignmentImageBitmapBasePerPixel: number;
  /** The alignment data but for the rendering (x, y, etc.). It's the aggregation from all tracks */
  orthologousGroups: OrthologousGroup[];
  /** Basic data for the currently active alignment */
  alignedFamily?: MiniFamily;
  /** Chosen member to start the alignment */
  activeMemberId: number | undefined;
  /** "full" doesn't exist anymore. Too much alignments shown on screen */
  alignmentDensity: AlignmentDensity;
  pileupRenderingMode: PileupRenderingMode;
  /* Show any feature shape popover on right click */
  showFeaturePopover: boolean;
  /** Must be complete. Is filled on shape hover + right click. */
  hoverData?: MainThreadHoverMessage;
  /** Data from the shape you are currently hovering, if any. If not, it can be the track only */
  contextualMenuData?: Partial<MainThreadHoverMessage>;
  /** Which synteny alignment type is currently active. Useful to know which tracks the lines are attached to. */
  activeSyntenyType?: SyntenyTypeEnum;
  asynchronousScrolling: boolean;
  syntenyWorker: Worker | undefined;
  /** When navigating from the feature tables */
  featureToNavigate: NavigationFeature | undefined;
  /** When loading a view */
  positionToNavigate: CanvasCoordinate | undefined;
  isResizingPanel: boolean;
  gridApiRef: RefObject<GridApi | null>;
}

export interface MainActions {
  setReady(canvas: HTMLCanvasElement): void; // Canvas is in the DOM
  updateCanvasSize: (width: number, height: number) => void;
  loadTracks: (orgId: number, initialContigs: number[], onError: (error: Error | FetchError) => void) => Promise<void>; // Load synteny atm
  reset: () => void;
  setAlignmentDensity(density: AlignmentDensity): void;
  resetAlignments: () => void;
  centerAlignment: () => void;
  toggleAsynchronousScrolling(): void;
  setPileupRenderingMode(pileupRenderingMode: PileupRenderingMode): void;
  isEveryTrackLoaded(): boolean;
  align: (to: MemberItem, options?: { forceCenter?: boolean }) => Promise<void>;
  alignActiveFamily: () => void;
  navigateToFeature: (item: NavigationFeature, forceAlign?: boolean) => Promise<void>;
  loadFromFeature: (
    item: components["schemas"]["FeatureLocationResponse"],
    organismId: number,
    resultType: GenomicFeatureTypeEnum,
    resultSubTypeId: number | undefined,
  ) => void;
}

export type MainSlice = MainActions & MainState;

export const initialMainState: MainState = {
  alignedFamily: undefined,
  activeMemberId: undefined,
  alignmentDensity: AlignmentDensityEnum.LOW,
  alignmentImageBitmap: undefined,
  alignmentImageBitmapBasePerPixel: 250,
  alignmentImageBitmapScrollX: 0,
  alignmentImageBitmapScrollY: 0,
  contextualMenuData: undefined,
  organisms: new Map(),
  orthologousGroups: [],
  showFeaturePopover: false,
  syntenyOrganism: {
    id: undefined,
    name: undefined,
    type: undefined,
  },
  trackGroups: new Map<string, TrackGroup>(),
  tracks: new Map<string, Track>(),
  activeSyntenyType: undefined,
  asynchronousScrolling: false,
  pileupRenderingMode: PileupRenderingModeEnum.CONDENSED,
  syntenyWorker: undefined,
  featureToNavigate: undefined,
  positionToNavigate: undefined,
  isResizingPanel: false,
  gridApiRef: { current: null },
};

export function updateAlignedFamilyMember(to: MemberItem, alignedFamily: MiniFamily | undefined): MiniFamily {
  const newMember: MinimalItem = {
    featureId: to.featureId,
    trackId: to.trackId,
    start: to.start,
    stop: to.stop,
  };

  if (!alignedFamily) {
    return {
      familyId: to.familyId,
      members: [newMember],
    };
  }

  const isSameFamily = alignedFamily.familyId === to.familyId;

  if (isSameFamily) {
    const newAlignedFamily = { ...alignedFamily };
    const idx = newAlignedFamily.members.findIndex((member) => member.trackId === to.trackId);

    if (idx > -1) {
      newAlignedFamily.members[idx] = newMember;
    } else {
      newAlignedFamily.members.push(newMember);
    }

    return newAlignedFamily;
  } else {
    return {
      familyId: to.familyId,
      members: [newMember],
    };
  }
}

export function buildFamilyFromFamilyData(
  familyMembers: Awaited<ReturnType<typeof fetchFeaturesFamilyMembersOrThrow>>,
  fromMemberItem: MemberItem,
  tracks: Tracks,
  trackGroups: TrackGroups,
  trackType?: TrackType,
  existingFamily?: MiniFamily,
): MiniFamily {
  const newFamily: MiniFamily = {
    familyId: fromMemberItem.familyId,
    members: [],
  };

  const organismContigToTrackGroups = new Map<string, string[]>();
  const trackGroupToTrackId = new Map<string, string>();

  for (const [groupId, trackGroup] of trackGroups) {
    const track = Array.from(tracks.values()).find(
      (track) => track.groupId === groupId && track.trackType === trackType,
    );

    if (!track) continue;

    trackGroupToTrackId.set(groupId, track.trackId);

    const key = `${trackGroup.organismId}:${trackGroup.contigId}`;
    if (!organismContigToTrackGroups.has(key)) {
      organismContigToTrackGroups.set(key, []);
    }
    organismContigToTrackGroups.get(key)!.push(groupId);
  }

  const existingMembersMap = new Map<string, MinimalItem>();
  if (existingFamily?.members) {
    for (const member of existingFamily.members) {
      existingMembersMap.set(member.trackId, member);
    }
  }

  for (const organism of familyMembers) {
    for (const contig of organism.contigs) {
      if (contig.members.length === 0) {
        continue;
      }

      const key = `${organism.organismId}:${contig.contigId}`;
      const trackGroupsForContig = organismContigToTrackGroups.get(key) || [];

      const sortedMembers = [...contig.members].sort((a, b) => a.start - b.start);

      for (let i = 0; i < trackGroupsForContig.length; i++) {
        const groupId = trackGroupsForContig[i];
        const trackId = trackGroupToTrackId.get(groupId);

        if (!trackId) continue;

        let assignedMemberId: number;
        let assignedMemberStart: number;
        let assignedMemberStop: number;
        const existingMember = existingMembersMap.get(trackId);

        if (trackId === fromMemberItem.trackId) {
          assignedMemberId = fromMemberItem.featureId;
          assignedMemberStart = fromMemberItem.start;
          assignedMemberStop = fromMemberItem.stop;
        } else if (existingMember) {
          assignedMemberId = existingMember.featureId;
          assignedMemberStart = existingMember.start;
          assignedMemberStop = existingMember.stop;
        } else {
          const sortedMember = sortedMembers[i < sortedMembers.length ? i : 0];
          assignedMemberId = sortedMember.locationId;
          assignedMemberStart = sortedMember.start;
          assignedMemberStop = sortedMember.stop;
        }

        newFamily.members.push({
          trackId,
          featureId: assignedMemberId,
          start: assignedMemberStart,
          stop: assignedMemberStop,
        });
      }
    }
  }

  return newFamily;
}

export function computeSyntenyTrackAlignmentsUtil(
  {
    fromMemberItem,
    trackType,
    trackGroups,
    tracks,
    alignedFamily,
    alignedTrackGroupStarts,
    newTrackGroups,
    familyMembers,
  }: ComputeSyntenyTrackAlignmentsData,
  forceOverviewRender: () => void,
): ComputeSyntenyTrackAlignmentsResult {
  const newAlignedTrackGroupStarts = new Map<string, number>();

  // No family
  if (familyMembers.length === 0) {
    return { newAlignedFamily: undefined, newAlignedTrackGroupStarts };
  }

  const isSameFamily = alignedFamily?.familyId != undefined && alignedFamily.familyId === fromMemberItem.familyId;

  if (isSameFamily && alignedFamily.members) {
    for (const member of alignedFamily.members) {
      const track = tracks.get(member.trackId);
      if (track) {
        const existingStart = alignedTrackGroupStarts?.get(track.groupId);

        if (existingStart != undefined) {
          newAlignedTrackGroupStarts.set(track.groupId, existingStart);
        }
      }
    }
  }

  const newAlignedFamily = buildFamilyFromFamilyData(
    familyMembers,
    fromMemberItem,
    tracks,
    trackGroups,
    trackType,
    isSameFamily ? alignedFamily : undefined,
  );

  const trackIdToOrthologId = new Map<string, number>();
  if (newAlignedFamily?.members) {
    // `fromMemberItem` is no longer part of server response. Add it if not already there!
    newAlignedFamily.members = newAlignedFamily.members.some((member) => member.trackId === fromMemberItem.trackId)
      ? newAlignedFamily.members
      : [...newAlignedFamily.members, fromMemberItem];
    for (const member of newAlignedFamily.members) {
      trackIdToOrthologId.set(member.trackId, member.featureId);
    }
  }

  const memberPositions = new Map<number, number>();
  const contigLeftMostPositions = new Map<string, number>();

  for (const organism of familyMembers) {
    for (const contig of organism.contigs) {
      if (contig.members.length > 0) {
        contigLeftMostPositions.set(
          `${organism.organismId}:${contig.contigId}`,
          Math.min(...contig.members.map((member) => member.start)),
        );
      }
      for (const member of contig.members) {
        memberPositions.set(member.locationId, member.start);
      }
    }
  }

  const activeGroupId = tracks.get(fromMemberItem.trackId)?.groupId;
  const activeBaseShift = trackGroups.get(activeGroupId ?? "--unknown--")?.graphics.baseShift ?? 0;

  for (const [groupId, group] of trackGroups) {
    const activeTrackInGroup = Array.from(tracks.values()).find(
      (track) => track.groupId === groupId && track.trackType === trackType,
    );

    const assignedOrthologId = activeTrackInGroup ? trackIdToOrthologId.get(activeTrackInGroup.trackId) : undefined;
    const assignedPosition = assignedOrthologId != undefined ? memberPositions.get(assignedOrthologId) : undefined;
    const contigLeftMostPosition = contigLeftMostPositions.get(`${group.organismId}:${group.contigId}`);
    const swapUpdate = newTrackGroups.find((g) => g.groupId === group.groupId);
    const swapToPosition = swapUpdate?.toBase;

    const targetPosition = assignedPosition ?? contigLeftMostPosition ?? swapToPosition;

    if (targetPosition == undefined) {
      continue;
    }

    newAlignedTrackGroupStarts.set(
      groupId,
      groupId === activeGroupId ? (fromMemberItem.start ?? targetPosition) : targetPosition,
    );

    if (groupId === activeGroupId) {
      continue;
    }

    // Preserve already-aligned groups when keeping the same family, unless we have a direct member assignment.
    if (assignedPosition == undefined && isSameFamily && alignedTrackGroupStarts?.has(groupId)) {
      continue;
    }

    group.graphics.baseShift = activeBaseShift + (fromMemberItem.start ?? 0) - targetPosition;
  }

  if (activeGroupId && fromMemberItem.start != undefined) {
    newAlignedTrackGroupStarts.set(activeGroupId, fromMemberItem.start);
  }

  forceOverviewRender();

  return {
    newAlignedFamily,
    newAlignedTrackGroupStarts,
  };
}

export const createMainSlice: StateCreator<GenomeViewerStore, [], [], MainSlice> = (set, get) => ({
  ...initialMainState,

  setReady(canvas) {
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    ctx.imageSmoothingEnabled = false;
    set({ canvas, ctx });
  },

  updateCanvasSize(width: number, height: number) {
    const { canvas } = get();
    if (canvas && (canvas.width !== width || canvas.height !== height)) {
      const ratio = 1; // window.devicePixelRatio;

      canvas.width = width * ratio;
      canvas.height = height * ratio;

      get().refreshTracks();
      get().refreshMain();
    }
  },

  loadTracks: createTracksLoader(set, get),

  reset() {
    const { tracks, ctx, alignmentImageBitmap, syntenyWorker } = get();

    for (const track of tracks.values()) {
      track.imageBitmap?.close();
      track.worker.terminate();
    }

    alignmentImageBitmap?.close();
    syntenyWorker?.terminate();

    ctx?.reset();

    set({
      ...initialMainState,
      ...initialCanvasState,
      ...initialRenderingState,
      ...initialTracksState,
      ...initialSelectionState,
      ...initialSaveLoadState,
      ...initialSearchState,
      ...initialUserEventsState,
    });
  },

  setAlignmentDensity(alignmentDensity) {
    set({ alignmentDensity });
    get().refreshTracks();
  },

  resetAlignments() {
    const trackGroups = get().trackGroups;
    for (const trackGroup of trackGroups) {
      trackGroup[1].graphics.baseShift = 0;
    }

    set({
      alignedFamily: undefined,
      alignedTrackGroupStarts: undefined,
      trackGroups: new Map(trackGroups),
    });
    get().refreshTracks();
  },

  centerAlignment() {
    const { alignedFamily, basePerPixel, canvas, trackGroups, tracks } = get();
    if (canvas == undefined || alignedFamily == undefined) {
      return;
    }

    if (alignedFamily.members.length === 0) {
      return;
    }

    const minMemberStart = Math.min(
      ...alignedFamily.members.map(
        ({ start, trackId }) => start + trackGroups.get(tracks.get(trackId)!.groupId)!.graphics.baseShift,
      ),
    );
    const maxMemberStop = Math.max(
      ...alignedFamily.members.map(
        ({ stop, trackId }) => stop + trackGroups.get(tracks.get(trackId)!.groupId)!.graphics.baseShift,
      ),
    );

    const maxFamilyWidth = Math.max(1, maxMemberStop - minMemberStart) / basePerPixel;
    let scale = 1;
    if (maxFamilyWidth >= canvas.width || maxFamilyWidth <= MIN_FOCUSED_SHAPE_WIDTH) {
      scale = Math.max((2 * maxFamilyWidth) / canvas.width, MIN_BASE_PER_PIXEL / basePerPixel); // Prevent having new basePerPixel < MIN_BASE_PER_PIXEL
    }

    const newScrollX = minMemberStart / basePerPixel / scale - (canvas.width - maxFamilyWidth / scale) / 2;

    set({
      scrollX: newScrollX,
      basePerPixel: basePerPixel * scale,
    });
    get().refreshTracks();
  },

  toggleAsynchronousScrolling() {
    set({
      asynchronousScrolling: !get().asynchronousScrolling,
    });
  },

  setPileupRenderingMode(pileupRenderingMode) {
    set({ orthologousGroups: [] });
    set({ pileupRenderingMode });
    get().refreshTracks();
  },

  isEveryTrackLoaded() {
    const activeSyntenyType = get().activeSyntenyType;
    const trackArr = [...get().tracks.values()].filter(
      (track) => track.isVisible && get().trackGroups.get(track.groupId)?.graphics.isVisible,
    );

    return (
      trackArr.length > 0 &&
      trackArr.every((track) => track.isLoaded) &&
      trackArr
        .filter((track) => track.trackType === getTrackTypeBySyntenyType(activeSyntenyType!))
        .every((track) => track.isOrthologsLoaded)
    );
  },

  async align(to: MemberItem, options?: { forceCenter?: boolean }) {
    const {
      tracks: _tracks,
      trackGroups: _groups,
      activeSyntenyType,
      updateTrackContigId,
      pileupRenderingMode,
      alignedFamily,
      syntenyOrganism,
    } = get();
    const trackType = _tracks.get(to.trackId)?.trackType;
    if (!trackType || !syntenyOrganism.id) {
      return;
    }
    let familyMembers;
    if (to.familyId) {
      const condense = pileupRenderingMode === PileupRenderingModeEnum.CONDENSED;
      familyMembers =
        activeSyntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT
          ? await fetchPangenomeAlignmentMembersOrThrow(syntenyOrganism.id, to.familyId)
          : await fetchFeaturesFamilyMembersOrThrow(to.familyId, condense);
    } else {
      familyMembers = await fetchPangenomeAlignmentMembersByFeatureOrThrow(
        syntenyOrganism.id,
        getGenomicFeatureTypeFromTrackType(trackType),
        to.featureId,
      );
    }

    const newTrackGroups = calculateContigSwaps(to, _groups, familyMembers, alignedFamily, _tracks);
    for (const { groupId, newContigId, toBase } of newTrackGroups) {
      updateTrackContigId(groupId, newContigId, toBase);
    }

    const { trackGroups, tracks } = get();
    const updatedAlignedFamily = updateAlignedFamilyMember(to, alignedFamily);
    const { newAlignedFamily, newAlignedTrackGroupStarts } = computeSyntenyTrackAlignmentsUtil(
      {
        fromMemberItem: to,
        trackType,
        trackGroups,
        tracks,
        alignedFamily: updatedAlignedFamily,
        alignedTrackGroupStarts: get().alignedTrackGroupStarts,
        newTrackGroups,
        familyMembers,
      },
      get().forceOverviewRender,
    );
    const storeUpdates: Partial<GenomeViewerState> = {
      alignedFamily: newAlignedFamily,
      alignedTrackGroupStarts: newAlignedTrackGroupStarts,
      activeMemberId: to.featureId,
    };

    set(storeUpdates);
    get().refreshTracks();

    if (options?.forceCenter) {
      get().centerAlignment();
    }
  },

  alignActiveFamily() {
    const { activeMemberId, alignedFamily, trackGroups, orthologousGroups } = get();
    // This util can only be called if there is already an aligned family existing
    if (!alignedFamily) return;

    // From the alignedFamily we get the OrthologData of each of its member, that contains more data (especially the start position)
    const activeMembers = orthologousGroups.find((orthoGroup) => orthoGroup.orthologId === alignedFamily.familyId);
    if (!activeMembers) return;

    // If the activeMemberId hasn't changed during a contig swap, get it's associated OrthologData. It will be used as the anchor member for aligning other trackGroups
    // Else set it to the first member of the activeMember
    const activeMember =
      activeMembers.orthologs.find((member) => member.id === activeMemberId) ?? activeMembers.orthologs[0];

    // This should never happen since the activeMembers.orthologs should contains at least 1 element
    if (!activeMember) return;

    // Updates the activeMemberId in the case it changed
    if (activeMember.id !== activeMemberId) {
      set({ activeMemberId: activeMember.id });
    }

    // activeBaseShift correspond to the baseShift of the trackGroup that contains the anchor member of the alignment
    const activeGroup = [...trackGroups.values()].find((group) => group.trackIds.includes(activeMember.trackId));
    if (!activeGroup) return;
    const activeBaseShift = activeGroup.graphics.baseShift;

    const alignedTrackGroupStarts = new Map(get().alignedTrackGroupStarts ?? []);

    for (const member of alignedFamily.members) {
      // This converts the members of the aligned family ({id, trackId}) to OrthologData, containing especially the start position
      const familyMember = activeMembers.orthologs.find((ortho) => ortho.id === member.featureId);
      if (!familyMember) continue;

      if (familyMember.id !== activeMemberId) {
        const trackGroup = [...trackGroups.values()].find((group) => group.trackIds.includes(familyMember.trackId));
        if (!trackGroup) continue;

        // Apply to the trackGroup the new baseShift to align with the anchor member
        trackGroup.graphics.baseShift = activeBaseShift + activeMember.start - familyMember.start;
        alignedTrackGroupStarts.set(trackGroup.groupId, familyMember.start);
      }
    }

    alignedTrackGroupStarts.set(activeGroup.groupId, activeMember.start);

    set({ trackGroups, alignedTrackGroupStarts });
    get().refreshTracks();
  },

  async navigateToFeature(item: NavigationFeature, forceAlign = false) {
    const { activeSyntenyType, ctx, trackGroups, tracks, basePerPixel, orthologousGroups } = get();

    const trackType = item.type ? getTrackTypeFromGenomicFeatureType(item.type) : TrackEnum.SEQUENCE; // Required to handle pangenome regions

    async function getNewTrackId(group: TrackGroup) {
      const subTypes = await getFeatureTypes(group.organismId);
      const subType = subTypes.nuFeatureTypes.find((subType) => subType.id === item.subTypeId);
      if (!subType) return;

      const newTrack: TrackConfig =
        trackType === TrackEnum.NUCLEOTIDE_FEATURE
          ? {
              trackType,
              group: group.groupId,
              options: {
                subType,
                color: getColorByTrackType(trackType),
                text: TrackTextEnum.INNER,
                isVisible: true,
              },
            }
          : {
              trackType,
              group: group.groupId,
            };
      return get()
        .createTracks([newTrack], () => {}, group.groupId)
        .values()
        .next().value?.trackId;
    }

    let trackGroup: TrackGroup;
    const trackGroupMatchingContigAndOrgAndTrackType = [...trackGroups.values()].find(
      (group) =>
        group.contigId === item.contigId &&
        group.organismId === item.orgId &&
        group.trackIds.some(
          (trackId) =>
            tracks.get(trackId)!.trackType === trackType && tracks.get(trackId)!.subType?.id === item.subTypeId,
        ),
    );

    let newTrackId: string | undefined = undefined;
    if (trackGroupMatchingContigAndOrgAndTrackType) {
      // Case 1: if organismId, contigId and trackType are matching
      trackGroup = trackGroupMatchingContigAndOrgAndTrackType;
      trackGroups.set(trackGroup.groupId, {
        ...trackGroup,
        graphics: { ...trackGroup.graphics, isVisible: true },
      });
    } else {
      const trackGroupMatchingOrgAndTrackType = [...trackGroups.values()].find(
        (group) =>
          group.organismId === item.orgId &&
          group.trackIds.some(
            (trackId) =>
              tracks.get(trackId)!.trackType === trackType && tracks.get(trackId)!.subType?.id === item.subTypeId,
          ),
      );

      if (trackGroupMatchingOrgAndTrackType) {
        // Case 2: if organismId and trackType are matching (without contigId) -> contig swap
        trackGroup = trackGroupMatchingOrgAndTrackType;
        // Ensure the group is visible
        trackGroups.set(trackGroup.groupId, {
          ...trackGroup,
          graphics: { ...trackGroup.graphics, isVisible: true },
        });
        get().updateTrackContigId(trackGroup.groupId, item.contigId);
      } else {
        const trackGroupMatchingOrgAndContigId = [...trackGroups.values()].find(
          (group) => group.organismId === item.orgId && group.contigId === item.contigId,
        );
        if (trackGroupMatchingOrgAndContigId) {
          // Case 3: if organismId and contigId are matching (without trackType) -> add new track of trackType
          newTrackId = await getNewTrackId(trackGroupMatchingOrgAndContigId);
          trackGroup = trackGroupMatchingOrgAndContigId;
          // Ensure the group is visible
          trackGroups.set(trackGroup.groupId, {
            ...trackGroup,
            graphics: { ...trackGroup.graphics, isVisible: true },
          });
        } else {
          const trackGroupMatchingOrg = [...trackGroups.values()].find((group) => group.organismId === item.orgId);
          if (trackGroupMatchingOrg) {
            // Case 4: if only organismId is matching (without contigId and trackType) -> contig swap + add new track
            trackGroup = trackGroupMatchingOrg;
            // Ensure the group is visible
            trackGroups.set(trackGroup.groupId, {
              ...trackGroup,
              graphics: { ...trackGroup.graphics, isVisible: true },
            });
            get().updateTrackContigId(trackGroup.groupId, item.contigId);
            newTrackId = await getNewTrackId(trackGroups.get(trackGroup.groupId)!);
          } else {
            // Case 5: nothing is matching -> add new trackGroup (of the right contigId) + add new track of trackType (this case shouldn't be possible since the table only shows features of present genomes)
            const newGroup = {
              contigId: item.contigId,
              genomeId: item.orgId,
              toBase: item.start ?? 0,
            };

            const subTypes = await getFeatureTypes(item.orgId);
            const subType = subTypes.nuFeatureTypes.find((subType) => subType.id === item.subTypeId);
            if (!subType) return;

            const newTrack: TrackConfig =
              trackType === TrackEnum.NUCLEOTIDE_FEATURE
                ? {
                    trackType,
                    group: newGroup,
                    options: {
                      subType,
                      color: getColorByTrackType(trackType),
                      text: TrackTextEnum.INNER,
                      isVisible: true,
                    },
                  }
                : {
                    trackType,
                    group: newGroup,
                  };
            const { groupId: newGroupId, trackId } = get()
              .createTracks([newTrack], () => {})
              .values()
              .next().value!;
            if (trackId) {
              newTrackId = trackId;
            }

            if (newGroupId && trackGroups.has(newGroupId)) {
              trackGroup = trackGroups.get(newGroupId)!;
            } else {
              // Case 6: Only if the creation of trackGroup failed (it should not happen)
              return;
            }
          }
        }
      }
      get().refreshTracks();
    }

    // Impossible to navigate to the specified feature in the current synteny set
    if (!trackGroup || !item.id || !item.start || !item.stop) {
      return;
    }

    const trackId =
      newTrackId ??
      trackGroup.trackIds.find((id) => {
        const track = tracks.get(id);
        if (!track) return false;

        const isTypeMatch = track.trackType === trackType;
        const isSubTypeMatch = item.subTypeId === undefined || track.subType?.id == item.subTypeId;

        return isTypeMatch && isSubTypeMatch;
      });

    if (!trackId) return;
    const track = tracks.get(trackId);
    if (!track) return;

    if (!track.isVisible) {
      tracks.set(trackId, { ...track, isVisible: true });
    }
    const itemWidth = (item.stop === item.start ? 1 : item.stop - item.start) / basePerPixel;
    const scale = Math.max((2 * itemWidth) / ctx!.canvas.width, MIN_BASE_PER_PIXEL / basePerPixel); // To prevent so that the basePerPixel becames lower than MIN_BASE_PER_PIXEL

    const totalHeight = get().getAllTracksHeight();
    const newScrollX =
      (trackGroup.graphics.baseShift + item.start) / basePerPixel / scale -
      ctx!.canvas.width / 2 +
      itemWidth / 2 / scale;

    const newScrollY = clamp(
      getTrackOffsetY(trackGroups, tracks, trackId) - ctx!.canvas.height / 2 + SHAPE_HEIGHT_WITHOUT_TEXT / 2,
      0,
      totalHeight - ctx!.canvas.height,
    );

    const storeUpdate: Partial<GenomeViewerState> = {
      scrollX: newScrollX,
      scrollY: newScrollY,
      basePerPixel: basePerPixel * scale,
      tracks,
      trackGroups,
      activeMemberId: forceAlign ? item.id : undefined,
    };

    // If the "Current Region" (table) changed, refresh its data
    if (Math.floor(get().scrollX) !== Math.floor(newScrollX)) {
      handleScrollingEnd(set);
      storeUpdate.isScrolling = true;
    }

    set(storeUpdate);

    if (forceAlign) {
      const familyId = orthologousGroups.find((family) =>
        family.orthologs.some((ortholog) => ortholog.id === item.id),
      )?.orthologId;
      if (familyId && track.trackType === getTrackTypeBySyntenyType(activeSyntenyType!)) {
        get().align(
          {
            featureId: item.id,
            trackId,
            trackGroupId: trackGroup.groupId,
            contigId: trackGroup.contigId,
            familyId: familyId,
            start: item.start,
            stop: item.stop,
          },
          {
            forceCenter: true,
          },
        );
      }
    }

    get().postTrackHighlightMessage(track, item.id);
    get().refreshTracks();
  },

  loadFromFeature(
    item: components["schemas"]["FeatureLocationResponse"],
    organismId: number,
    resultType: GenomicFeatureTypeEnum,
    resultSubTypeId: number | undefined,
  ) {
    const { trackGroups, tracks, createTracks } = get();

    const trackType = getTrackTypeFromGenomicFeatureType(resultType);

    const newTrackConfigs: TrackConfig[] = [];
    for (const trackGroup of [...trackGroups.values()]) {
      // 1. Check if there is already no track with the same trackType
      if (
        !trackGroup?.trackIds.some(
          (trackId) =>
            tracks.get(trackId)?.trackType === trackType || tracks.get(trackId)?.subType?.id === resultSubTypeId,
        )
      ) {
        // 2. If nuFeature check if the trackGroup has the current featureType and add it as track
        if (trackType === TrackEnum.NUCLEOTIDE_FEATURE) {
          getFeatureTypes(trackGroup.organismId).then((subTypes) => {
            const subType = subTypes.nuFeatureTypes.find((subType) => subType.id === resultSubTypeId);
            if (subType) {
              createTracks(
                [
                  {
                    group: trackGroup.groupId,
                    trackType: trackType,
                    options: {
                      subType,
                    } as NUFeatureOptions,
                  },
                ],
                () => {},
              );
              get().refreshTracks();
            }
          });
        } else {
          // 3. For classical trackTypes, add it as a track
          newTrackConfigs.push({
            group: trackGroup.groupId,
            trackType: trackType,
          });
        }
      }
    }

    if (newTrackConfigs.length > 0) {
      createTracks(newTrackConfigs, () => {});
      get().refreshTracks();
    }

    set({
      featureToNavigate: {
        id: item.id,
        contigId: item.contigId,
        orgId: organismId,
        type: resultType,
        start: item.range.start,
        stop: item.range.stop,
      },
    });
  },
});
